import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  PlayArrow,
  Stop,
  CheckCircle,
  MoreVert,
  FilterList,
  Search,
} from '@mui/icons-material';

import { maintenanceService } from '../../services/maintenanceService';
import { useSiteContext } from '../../hooks/useSiteContext';
import {
  MaintenanceWorkOrder,
  CreateMaintenanceWorkOrder,
  MaintenanceWorkOrderFilters,
  PaginatedResponse,
  Priority,
  WorkOrderStatus,
} from '../../types/maintenance';

const MaintenanceWorkOrders: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [workOrders, setWorkOrders] = useState<MaintenanceWorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filtering
  const [filters, setFilters] = useState<MaintenanceWorkOrderFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<MaintenanceWorkOrder | null>(null);
  const [workOrderForm, setWorkOrderForm] = useState<Partial<CreateMaintenanceWorkOrder>>({
    title: '',
    description: '',
    priority: Priority.NORMAL,
    status: WorkOrderStatus.PLANNED,
  });

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWorkOrderId, setMenuWorkOrderId] = useState<number | null>(null);

  // Load work orders
  const loadWorkOrders = async () => {
    if (!currentSite.siteId) return;

    try {
      setLoading(true);
      setError(null);

      const searchFilters: MaintenanceWorkOrderFilters = {
        ...filters,
        site: currentSite.siteId.toString(),
        page: page + 1,
        page_size: rowsPerPage,
      };

      if (searchTerm) {
        searchFilters.search = searchTerm;
      }

      const response: PaginatedResponse<MaintenanceWorkOrder> = 
        await maintenanceService.workOrders.getWorkOrders(searchFilters);

      setWorkOrders(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      console.error('Failed to load work orders:', err);
      setError('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkOrders();
  }, [currentSite.siteId, page, rowsPerPage, filters, searchTerm]);

  const handleCreateWorkOrder = async () => {
    try {
      await maintenanceService.workOrders.createWorkOrder(workOrderForm as CreateMaintenanceWorkOrder);
      setCreateDialogOpen(false);
      resetForm();
      loadWorkOrders();
    } catch (err) {
      console.error('Failed to create work order:', err);
    }
  };

  const handleEditWorkOrder = async () => {
    if (!selectedWorkOrder) return;

    try {
      await maintenanceService.workOrders.updateWorkOrder(selectedWorkOrder.id, workOrderForm);
      setEditDialogOpen(false);
      resetForm();
      loadWorkOrders();
    } catch (err) {
      console.error('Failed to update work order:', err);
    }
  };

  const handleDeleteWorkOrder = async (id: number) => {
    try {
      await maintenanceService.workOrders.deleteWorkOrder(id);
      loadWorkOrders();
    } catch (err) {
      console.error('Failed to delete work order:', err);
    }
  };

  const handleStartWorkOrder = async (id: number) => {
    try {
      await maintenanceService.workOrders.startWorkOrder(id);
      loadWorkOrders();
    } catch (err) {
      console.error('Failed to start work order:', err);
    }
  };

  const handleCompleteWorkOrder = async (id: number) => {
    try {
      await maintenanceService.workOrders.completeWorkOrder(id);
      loadWorkOrders();
    } catch (err) {
      console.error('Failed to complete work order:', err);
    }
  };

  const resetForm = () => {
    setWorkOrderForm({
      title: '',
      description: '',
      priority: Priority.NORMAL,
      status: WorkOrderStatus.PLANNED,
    });
    setSelectedWorkOrder(null);
  };

  const openEditDialog = (workOrder: MaintenanceWorkOrder) => {
    setSelectedWorkOrder(workOrder);
    setWorkOrderForm({
      title: workOrder.title,
      description: workOrder.description,
      priority: workOrder.priority,
      status: workOrder.status,
      equipment: workOrder.equipment,
      assigned_to: workOrder.assigned_to,
      scheduled_start: workOrder.scheduled_start_time,
      estimated_hours: workOrder.estimated_hours,
    });
    setEditDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (!currentSite.siteId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a site to view work orders.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Work Orders</Typography>
        <Box>
          <Button
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Work Order
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search work orders..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            {showFilters && (
              <>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status?.[0] || ''}
                      label="Status"
                      onChange={(e) => {
                        const value = e.target.value as string;
                        setFilters({ 
                          ...filters, 
                          status: value ? [value as WorkOrderStatus] : undefined 
                        });
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value={WorkOrderStatus.PLANNED}>Planned</MenuItem>
                      <MenuItem value={WorkOrderStatus.SCHEDULED}>Scheduled</MenuItem>
                      <MenuItem value={WorkOrderStatus.IN_PROGRESS}>In Progress</MenuItem>
                      <MenuItem value={WorkOrderStatus.ON_HOLD}>On Hold</MenuItem>
                      <MenuItem value={WorkOrderStatus.COMPLETED}>Completed</MenuItem>
                      <MenuItem value={WorkOrderStatus.CANCELLED}>Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={filters.priority?.[0] || ''}
                      label="Priority"
                      onChange={(e) => {
                        const value = e.target.value as string;
                        setFilters({ 
                          ...filters, 
                          priority: value ? [value as Priority] : undefined 
                        });
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value={Priority.LOW}>Low</MenuItem>
                      <MenuItem value={Priority.NORMAL}>Normal</MenuItem>
                      <MenuItem value={Priority.HIGH}>High</MenuItem>
                      <MenuItem value={Priority.URGENT}>Urgent</MenuItem>
                      <MenuItem value={Priority.CRITICAL}>Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Due Date From"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setFilters({ ...filters, date_range_after: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Due Date To"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setFilters({ ...filters, date_range_before: e.target.value })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Work Orders Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Scheduled Start</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workOrders.map((workOrder) => (
                    <TableRow key={workOrder.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {workOrder.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {workOrder.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {workOrder.equipment_name || 'Not assigned'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workOrder.status}
                          color={getStatusColor(workOrder.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workOrder.priority}
                          color={getPriorityColor(workOrder.priority) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {workOrder.assigned_to_detail?.username || 'Unassigned'}
                      </TableCell>
                      <TableCell>
                        {workOrder.scheduled_start_time 
                          ? new Date(workOrder.scheduled_start_time).toLocaleDateString()
                          : 'Not scheduled'
                        }
                      </TableCell>
                      <TableCell>
                        {workOrder.estimated_hours 
                          ? `${workOrder.estimated_hours} hrs`
                          : 'Not set'
                        }
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {workOrder.status === WorkOrderStatus.PLANNED && (
                            <Tooltip title="Start Work Order">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleStartWorkOrder(workOrder.id)}
                              >
                                <PlayArrow />
                              </IconButton>
                            </Tooltip>
                          )}
                          {workOrder.status === 'in_progress' && (
                            <Tooltip title="Complete Work Order">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleCompleteWorkOrder(workOrder.id)}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setMenuWorkOrderId(workOrder.id);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {workOrders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No work orders found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            const workOrder = workOrders.find(wo => wo.id === menuWorkOrderId);
            if (workOrder) openEditDialog(workOrder);
            setAnchorEl(null);
          }}
        >
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            // TODO: Navigate to detail view
            setAnchorEl(null);
          }}
        >
          <Visibility sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuWorkOrderId) handleDeleteWorkOrder(menuWorkOrderId);
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Work Order Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Work Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={workOrderForm.title}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={workOrderForm.description}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={workOrderForm.priority}
                  label="Priority"
                  onChange={(e) => setWorkOrderForm({ ...workOrderForm, priority: e.target.value as Priority })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Hours"
                value={workOrderForm.estimated_hours || ''}
                onChange={(e) => setWorkOrderForm({ 
                  ...workOrderForm, 
                  estimated_hours: parseInt(e.target.value) 
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Scheduled Start"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setWorkOrderForm({ 
                  ...workOrderForm, 
                  scheduled_start: new Date(e.target.value).toISOString()
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateWorkOrder} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Work Order Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Work Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={workOrderForm.title}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={workOrderForm.description}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={workOrderForm.priority}
                  label="Priority"
                  onChange={(e) => setWorkOrderForm({ ...workOrderForm, priority: e.target.value as Priority })}
                >
                  <MenuItem value={Priority.LOW}>Low</MenuItem>
                  <MenuItem value={Priority.NORMAL}>Normal</MenuItem>
                  <MenuItem value={Priority.HIGH}>High</MenuItem>
                  <MenuItem value={Priority.URGENT}>Urgent</MenuItem>
                  <MenuItem value={Priority.CRITICAL}>Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={workOrderForm.status}
                  label="Status"
                  onChange={(e) => setWorkOrderForm({ ...workOrderForm, status: e.target.value as WorkOrderStatus })}
                >
                  <MenuItem value={WorkOrderStatus.PLANNED}>Planned</MenuItem>
                  <MenuItem value={WorkOrderStatus.SCHEDULED}>Scheduled</MenuItem>
                  <MenuItem value={WorkOrderStatus.IN_PROGRESS}>In Progress</MenuItem>
                  <MenuItem value={WorkOrderStatus.ON_HOLD}>On Hold</MenuItem>
                  <MenuItem value={WorkOrderStatus.COMPLETED}>Completed</MenuItem>
                  <MenuItem value={WorkOrderStatus.CANCELLED}>Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditWorkOrder} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceWorkOrders; 